import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Briefcase, Search, Plus, RotateCcw, DollarSign } from "lucide-react";
import { formatCurrency } from "@/lib/format";
import { pretty, LANES, OPP_STAGE_HEX, REVENUE_HEX, hexWithAlpha, GRAY, NAVY, GREEN, AMBER, PURPLE } from "./crmConstants";
import RevenueOutcomeDialog from "./RevenueOutcomeDialog";

const StatCard = ({ label, value, color, testid }) => (
  <div className="rounded-xl border border-[#EEF3F6] px-3 py-3" style={{ backgroundColor: hexWithAlpha(color, 0.1) }} data-testid={testid}>
    <div className="text-xl font-bold tabular-nums font-display leading-none" style={{ color }}>{value}</div>
    <div className="text-[11px] text-[#4B5563] mt-1">{label}</div>
  </div>
);

const EMPTY_NEW = {
  name: "", service_lane: "sell_home", stage: "opportunity_identified", revenue_status: "not_started",
  estimated_value: "", expected_close_date: "", notes: "", lead_id: "", relationship_id: "",
};

export default function CrmOpportunities() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({
    q: "", stage: sp.get("stage") || "all", service_lane: "all",
    revenue_status: sp.get("revenue_status") || "all", territory_id: "all", franchise_id: "all",
  });
  const [open, setOpen] = useState(sp.get("new") === "1");
  const [nu, setNu] = useState({ ...EMPTY_NEW, relationship_id: sp.get("relationship_id") || "", lead_id: sp.get("lead_id") || "" });
  const [saving, setSaving] = useState(false);
  const [rev, setRev] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        q: f.q || undefined,
        stage: f.stage === "all" ? undefined : f.stage,
        service_lane: f.service_lane === "all" ? undefined : f.service_lane,
        revenue_status: f.revenue_status === "all" ? undefined : f.revenue_status,
        territory_id: f.territory_id === "all" ? undefined : f.territory_id,
        franchise_id: f.franchise_id === "all" ? undefined : f.franchise_id,
      };
      const { data } = await api.get("/crm/opportunities", { params });
      setData(data);
    } catch { toast.error("Could not load opportunities."); }
    finally { setLoading(false); }
  }, [f]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!nu.name.trim()) { toast.error("Name is required."); return; }
    setSaving(true);
    try {
      const payload = { ...nu, estimated_value: nu.estimated_value ? Number(nu.estimated_value) : null };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      const { data } = await api.post("/crm/opportunities", payload);
      toast.success("Opportunity created.");
      setOpen(false); setSp({});
      navigate(`/crm/opportunities/${data.opportunity.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create."); }
    finally { setSaving(false); }
  };

  const stages = data?.stages || [];
  const revStatuses = data?.revenue_statuses || [];
  const counts = data?.counts || {};
  const names = data?.user_names || {};
  const territories = data?.territories || [];
  const franchises = data?.franchises || [];
  const activeCount = stages.filter((s) => s !== "closed_won" && s !== "closed_lost").reduce((a, s) => a + (counts[s] || 0), 0);

  return (
    <div className="pb-12" data-testid="crm-opportunities">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <Briefcase size={22} className="text-[#1C4358]" />
          <h1 className="text-2xl font-display font-bold text-[#1C4358]">Opportunities</h1>
          {data && <span className="text-[13px] text-[#4B5563]">{data.items.length} deals</span>}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} className="border-[#D6E0E7] text-[#1C4358] gap-1"><RotateCcw size={14} /> Refresh</Button>
          <Button size="sm" data-testid="opp-new-btn" onClick={() => setOpen(true)} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1"><Plus size={15} /> New Opportunity</Button>
        </div>
      </div>

      {/* Summary cards (revenue placeholders — internal only) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard label="Pipeline value (est.)" value={formatCurrency(data?.pipeline_value || 0)} color={NAVY} testid="opp-stat-pipeline" />
        <StatCard label="Pending revenue" value={formatCurrency(data?.pending_value || 0)} color={AMBER} testid="opp-stat-pending" />
        <StatCard label="Received revenue" value={formatCurrency(data?.received_value || 0)} color={GREEN} testid="opp-stat-received" />
        <StatCard label="Active deals" value={activeCount} color={PURPLE} testid="opp-stat-active" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative"><Search size={15} className="absolute left-2.5 top-2.5 text-[#4B5563]" /><Input data-testid="opp-search" value={f.q} onChange={(e) => setF((p) => ({ ...p, q: e.target.value }))} placeholder="Search opportunities" className="pl-8 h-9 w-56 text-[14px] rounded-lg border-[#1C4358]/20" /></div>
        <Select value={f.stage} onValueChange={(v) => setF((p) => ({ ...p, stage: v }))}><SelectTrigger data-testid="opp-filter-stage" className="w-[180px] h-9 text-[13px]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All stages</SelectItem>{stages.map((s) => <SelectItem key={s} value={s}>{pretty(s)}</SelectItem>)}</SelectContent></Select>
        <Select value={f.service_lane} onValueChange={(v) => setF((p) => ({ ...p, service_lane: v }))}><SelectTrigger data-testid="opp-filter-lane" className="w-[170px] h-9 text-[13px]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All service lanes</SelectItem>{LANES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent></Select>
        <Select value={f.revenue_status} onValueChange={(v) => setF((p) => ({ ...p, revenue_status: v }))}><SelectTrigger data-testid="opp-filter-revenue" className="w-[170px] h-9 text-[13px]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All revenue status</SelectItem>{revStatuses.map((s) => <SelectItem key={s} value={s}>{pretty(s)}</SelectItem>)}</SelectContent></Select>
        <Select value={f.territory_id} onValueChange={(v) => setF((p) => ({ ...p, territory_id: v }))}><SelectTrigger data-testid="opp-filter-territory" className="w-[160px] h-9 text-[13px]"><SelectValue placeholder="All territories" /></SelectTrigger><SelectContent><SelectItem value="all">All territories</SelectItem>{territories.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select>
        <Select value={f.franchise_id} onValueChange={(v) => setF((p) => ({ ...p, franchise_id: v }))}><SelectTrigger data-testid="opp-filter-franchise" className="w-[160px] h-9 text-[13px]"><SelectValue placeholder="All franchises" /></SelectTrigger><SelectContent><SelectItem value="all">All franchises</SelectItem>{franchises.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select>
      </div>

      {loading && !data ? <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div> :
        (data.items || []).length === 0 ? <div className="text-center py-16 text-[#4B5563]"><Briefcase className="mx-auto mb-2 text-[#9AA5B1]" /> No opportunities yet. Create your first deal.</div> : (
          <div className="space-y-2">
            {data.items.map((o) => {
              const hex = OPP_STAGE_HEX[o.stage] || GRAY;
              const rhex = REVENUE_HEX[o.revenue_status] || GRAY;
              const val = o.actual_value || o.estimated_value;
              return (
                <div key={o.id} data-testid={`opp-row-${o.id}`} onClick={() => navigate(`/crm/opportunities/${o.id}`)}
                  className="flex items-center justify-between gap-3 rounded-xl border border-[#E6EDF2] bg-white p-3 flex-wrap hover:border-[#1C4358]/40 transition-colors cursor-pointer">
                  <div className="min-w-0">
                    <div className="font-semibold text-[#1C4358] text-[15px] truncate">{o.name}</div>
                    <div className="text-[12px] text-[#4B5563] flex items-center gap-2 flex-wrap mt-0.5">
                      {o.service_lane && <span>{pretty(o.service_lane)}</span>}
                      {names[o.managed_by_user_id] && <span>· {names[o.managed_by_user_id]}</span>}
                      {o.expected_close_date && <span>· close {(o.expected_close_date || "").slice(0, 10)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {val ? <span className="text-[14px] font-bold text-[#252221] tabular-nums flex items-center gap-0.5"><DollarSign size={13} className="text-[#2F7D5A]" />{formatCurrency(val)}</span> : null}
                    <span className="text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ backgroundColor: hexWithAlpha(rhex, 0.14), color: rhex }}>{pretty(o.revenue_status)}</span>
                    <span className="text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ backgroundColor: hexWithAlpha(hex, 0.14), color: hex }}>{pretty(o.stage)}</span>
                    <button data-testid={`opp-row-revenue-${o.id}`} title="Add revenue outcome"
                      onClick={(e) => { e.stopPropagation(); setRev({ opportunityId: o.id, leadId: o.lead_id, contextLabel: o.name, defaults: { service_lane: o.service_lane, relationship_id: o.relationship_id, estimated_value: o.estimated_value, actual_value: o.actual_value, revenue_status: o.revenue_status } }); }}
                      className="h-7 w-7 flex items-center justify-center rounded-md text-[#2F7D5A] hover:bg-[rgba(47,125,90,0.12)] transition-colors"><DollarSign size={15} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSp({}); }}>
        <DialogContent className="max-w-lg" data-testid="opp-create-dialog">
          <DialogHeader><DialogTitle className="font-display text-[#1C4358]">New Opportunity</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="sm:col-span-2"><label className="text-[11px] text-[#4B5563]">Name *</label><Input data-testid="opp-new-name" value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} placeholder="Sell 123 Oak St + placement" className="h-9 text-[14px]" /></div>
            <div><label className="text-[11px] text-[#4B5563]">Service lane</label>
              <Select value={nu.service_lane} onValueChange={(v) => setNu({ ...nu, service_lane: v })}><SelectTrigger className="h-9 text-[13px]"><SelectValue /></SelectTrigger><SelectContent>{LANES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><label className="text-[11px] text-[#4B5563]">Stage</label>
              <Select value={nu.stage} onValueChange={(v) => setNu({ ...nu, stage: v })}><SelectTrigger data-testid="opp-new-stage" className="h-9 text-[13px]"><SelectValue /></SelectTrigger><SelectContent>{stages.map((s) => <SelectItem key={s} value={s}>{pretty(s)}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><label className="text-[11px] text-[#4B5563]">Estimated value ($)</label><Input data-testid="opp-new-value" type="number" value={nu.estimated_value} onChange={(e) => setNu({ ...nu, estimated_value: e.target.value })} className="h-9 text-[14px]" /></div>
            <div><label className="text-[11px] text-[#4B5563]">Revenue status</label>
              <Select value={nu.revenue_status} onValueChange={(v) => setNu({ ...nu, revenue_status: v })}><SelectTrigger className="h-9 text-[13px]"><SelectValue /></SelectTrigger><SelectContent>{revStatuses.map((s) => <SelectItem key={s} value={s}>{pretty(s)}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><label className="text-[11px] text-[#4B5563]">Expected close date</label><Input type="date" value={nu.expected_close_date} onChange={(e) => setNu({ ...nu, expected_close_date: e.target.value })} className="h-9 text-[13px]" /></div>
            <div><label className="text-[11px] text-[#4B5563]">Link lead ID (optional)</label><Input value={nu.lead_id} onChange={(e) => setNu({ ...nu, lead_id: e.target.value })} placeholder="lead id" className="h-9 text-[13px]" /></div>
            <div className="sm:col-span-2"><label className="text-[11px] text-[#4B5563]">Notes</label><Textarea value={nu.notes} onChange={(e) => setNu({ ...nu, notes: e.target.value })} rows={2} className="text-[14px]" /></div>
            {nu.relationship_id && <div className="sm:col-span-2 text-[12px] text-[#2F7D5A]">Linked to partner/vendor relationship.</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setSp({}); }} className="border-[#D6E0E7] text-[#1C4358]">Cancel</Button>
            <Button data-testid="opp-create-submit" onClick={create} disabled={saving} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1">{saving && <Loader2 size={14} className="animate-spin" />} Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {rev && (
        <RevenueOutcomeDialog open={!!rev} onOpenChange={(v) => !v && setRev(null)}
          opportunityId={rev.opportunityId} leadId={rev.leadId} defaults={rev.defaults} contextLabel={rev.contextLabel}
          onSaved={() => { setRev(null); load(); }} />
      )}
    </div>
  );
}
